import React, { useState, useRef, useEffect } from 'react';
import { Search, AlignLeft, Link2, Hash, Calendar, DollarSign } from 'lucide-react';

interface SortField {
  id: string;
  name: string;
  type?: string;
}

interface SortMenuProps {
  isOpen: boolean;
  onClose: () => void;
  anchorEl: HTMLElement | null;
  fields: SortField[];
  onSelectField: (fieldId: string) => void;
}

const getFieldIcon = (fieldName: string, type?: string) => {
  const name = fieldName.toLowerCase();

  if (name.includes('url') || name.includes('link')) {
    return Link2;
  }
  if (name.includes('date') || name.includes('time')) {
    return Calendar;
  }
  if (name.includes('price') || name.includes('amount') || name.includes('revenue')) {
    return DollarSign;
  }
  if (type === 'number' || name.includes('count') || name.includes('number')) {
    return Hash;
  }

  return AlignLeft;
};

export const SortMenu: React.FC<SortMenuProps> = ({
  isOpen,
  onClose,
  anchorEl,
  fields,
  onSelectField
}) => {
  const popoverRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  // Position popover near anchor
  useEffect(() => {
    if (isOpen && anchorEl && popoverRef.current) {
      const anchorRect = anchorEl.getBoundingClientRect();
      const popover = popoverRef.current;

      popover.style.top = `${anchorRect.bottom + 8}px`;
      popover.style.left = `${anchorRect.left}px`;

      // Trigger animation
      requestAnimationFrame(() => setIsVisible(true));

      // Focus search input
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
    } else {
      setIsVisible(false);
    }
  }, [isOpen, anchorEl]);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node) &&
        anchorEl &&
        !anchorEl.contains(e.target as Node)
      ) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose, anchorEl]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }

      const filtered = filteredFields;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, filtered.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (filtered[selectedIndex]) {
          handleSelectField(filtered[selectedIndex].id);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, selectedIndex]);

  // Reset when closing
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setSelectedIndex(0);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const filteredFields = fields.filter(field =>
    field.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelectField = (fieldId: string) => {
    onSelectField(fieldId);
  };

  return (
    <>
      <style>{`
        @keyframes menuIn {
          from { opacity: 0; transform: translateY(-4px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>

      <div
        ref={popoverRef}
        role="menu"
        aria-labelledby="sort-menu-title"
        className="fixed bg-white rounded-xl border border-gray-200 p-3"
        style={{
          width: '260px',
          maxWidth: 'calc(100vw - 32px)',
          boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05), 0 10px 15px -3px rgba(0,0,0,0.08), 0 20px 25px -5px rgba(0,0,0,0.05)',
          zIndex: 10000,
          animation: isVisible ? 'menuIn 150ms ease-out forwards' : 'none',
          opacity: isVisible ? 1 : 0
        }}
      >
        {/* Header */}
        <h3 className="text-[12px] font-semibold text-gray-900 mb-2 px-1">
          Sort by
        </h3>

        {/* Search input */}
        <div className="relative mb-2">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Find a field"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setSelectedIndex(0);
            }}
            aria-label="Find a field to sort by"
            className="w-full h-8 pl-8 pr-3 text-[12px] text-gray-900 bg-gray-50 border border-gray-200 rounded-lg outline-none transition-all placeholder:text-gray-400 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
          />
        </div>

        {/* Sortable fields list */}
        <ul
          ref={listRef}
          className="max-h-[240px] overflow-y-auto -mx-1"
        >
          {filteredFields.length === 0 ? (
            <li className="px-3 py-4 text-[12px] text-gray-400 text-center">
              No fields found
            </li>
          ) : (
            filteredFields.map((field, index) => {
              const Icon = getFieldIcon(field.name, field.type);
              const isSelected = index === selectedIndex;

              return (
                <li
                  key={field.id}
                  role="menuitemradio"
                  aria-checked={isSelected}
                  onClick={() => handleSelectField(field.id)}
                  onMouseEnter={() => setSelectedIndex(index)}
                  className={`flex items-center gap-2.5 px-2.5 py-2 mx-1 rounded-lg cursor-pointer transition-colors ${
                    isSelected ? 'bg-gray-100' : 'hover:bg-gray-50'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                  <span className="text-[12px] font-medium text-gray-700 flex-1 truncate">
                    {field.name}
                  </span>
                </li>
              );
            })
          )}
        </ul>
      </div>
    </>
  );
};
